import React, { useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import debounce from 'lodash/debounce';
import { useTranslation } from 'react-i18next';
import ChatEmptyIcon from 'components/atoms/icons/chatEmptyIcon';
import Avatar from 'components/atoms/smsDashboard/avatar';
import 'react-loading-skeleton/dist/skeleton.css';
import BaseButton from 'components/atoms/baseButton';
import EmailCreateIcon from 'components/atoms/icons/emailCreateIcon';
import { usePatientSearch } from 'infrastructure/hooks/patient/use-patient-search';
import { CONVERSATION_TYPES } from 'store/smsDashboard';

import s from './styles.module.scss';

import type {
  PatientOptionType,
  PatientSearchInputOptionType,
  PatientSearchPatientType,
} from 'infrastructure/interfaces';

interface IPatientSearchProps {
  value: any;
  setValue: (data: PatientOptionType) => void;
  disabled: boolean;
  onClick: (data: PatientOptionType) => void;
  conversationType: string;
}

const generateLabel = (item: PatientSearchPatientType) => ({
  fullName: ` ${item.firstName} ${item.lastName} `,
  phone: `${item.phone}`,
  birthDate: `${item.birthDate}`,
});

const debouncedSearch = debounce(async ({ searchQuery, setValue }) => {
  setValue({
    phoneNumber: searchQuery,
  });
}, 800);

const PatientSearch: React.FC<IPatientSearchProps> = (props) => {
  const { value, setValue, disabled, onClick, conversationType } = props;
  const [searchValue, setSearchValue] = useState('');
  const { t } = useTranslation();

  const { patientSearch, isFetching, isLoading } = usePatientSearch({
    searchQuery: value,
  });

  const handleOptionClick = (option: PatientSearchInputOptionType) => {
    onClick({ phoneNumber: option.value, patientGuid: option.id });
  };
  const isUnregisteredConversationType =
    conversationType === CONVERSATION_TYPES.UNREGISTERED_RPM ||
    conversationType === CONVERSATION_TYPES.UNREGISTERED_CCM;

  const isNotUnregisteredConversationType =
    conversationType !== CONVERSATION_TYPES.UNREGISTERED_RPM &&
    conversationType !== CONVERSATION_TYPES.UNREGISTERED_CCM;

  const handleInputChange = (string: string) => {
    if (isUnregisteredConversationType) {
      const inputValue =
        string !== ''
          ? `+${string
              .replace(/[a-zA-Z ]/g, '')
              .replace(/\+/g, '')
              .slice(0, 11)}`
          : '';

      setSearchValue(inputValue);

      if (
        (inputValue !== '+' && inputValue.length === 12) ||
        inputValue.length === 0
      ) {
        debouncedSearch({
          searchQuery: inputValue,
          setValue,
        });
      }
    }
    if (isNotUnregisteredConversationType) {
      setSearchValue(string);

      debouncedSearch({
        searchQuery: string,
        setValue,
      });
    }
  };

  const isPatientFound = !isFetching && !isLoading && patientSearch?.length > 0;
  const isPatientNotFound = !isFetching && patientSearch?.length === 0;

  const searchInputPlaceholder = isUnregisteredConversationType
    ? t('smsDashboard.type_number')
    : t('smsDashboard.type_number_or_name');

  const notFoundEmptyMessage = isUnregisteredConversationType
    ? searchInputPlaceholder
    : t('smsDashboard.unregistered_phone_number_message');

  const emptyMessage =
    value.length > 0 ? notFoundEmptyMessage : searchInputPlaceholder;

  return (
    <div className={s['patient-name-phone']}>
      <input
        className={`${s['chat-input']} ${s['new-conversation-input']}`}
        placeholder={searchInputPlaceholder}
        type="text"
        onChange={(e) => handleInputChange(e.target.value)}
        value={searchValue}
        disabled={disabled}
      />
      {isPatientFound && !isUnregisteredConversationType && (
        <div className={s['patient-list']}>
          {!isUnregisteredConversationType &&
            patientSearch?.map((option: PatientSearchPatientType) => (
              <div className={s['patient-item']} key={option.guid}>
                <div className={s['patient-item-info']}>
                  <Avatar value={generateLabel(option).fullName} width={32} />
                  <div className={s['patient-item-data']}>
                    <div className={s['patient-item-name']}>
                      <span>{generateLabel(option).fullName}</span>
                      <span className={s['patient-item-birthdate']}>
                        {generateLabel(option).birthDate &&
                          generateLabel(option).birthDate}
                      </span>
                    </div>
                  </div>
                </div>

                <div className={s['patient-item-phone']}>
                  <div>Primary number:</div>
                  {option.phone ? (
                    <>
                      <div className={s['patient-item-phone-number']}>
                        &ensp;{option.phone}
                      </div>
                      <BaseButton
                        icon={<EmailCreateIcon />}
                        type="secondary"
                        onClick={() =>
                          handleOptionClick({
                            ...option,
                            value: option.phone,
                            id: option.guid,
                          })
                        }
                        disabled={!option.phone || disabled}
                      />
                    </>
                  ) : (
                    <div className={s['patient-item-phone-number']}>
                      &ensp;-
                    </div>
                  )}
                </div>
                <div className={s['patient-item-phone']}>
                  <div>Secondary number:</div>
                  {option.homeNumber ? (
                    <>
                      <div className={s['patient-item-phone-number']}>
                        &ensp;{option.homeNumber}
                      </div>
                      <BaseButton
                        icon={<EmailCreateIcon />}
                        type="secondary"
                        onClick={() =>
                          handleOptionClick({
                            ...option,
                            value: option.homeNumber,
                            id: option.guid,
                          })
                        }
                        disabled={!option.homeNumber || disabled}
                      />
                    </>
                  ) : (
                    <div className={s['patient-item-phone-number']}>
                      &ensp;-
                    </div>
                  )}
                </div>
              </div>
            ))}
        </div>
      )}
      {isFetching && (
        <div className={s['patient-list']}>
          {[...Array(7)].map((_, index) => (
            <div className={s['patient-item']} key={index}>
              <div className={s['patient-item-info']}>
                <Skeleton circle height={32} width={32} />
                <div className={s['patient-item-data']}>
                  <div className={s['patient-item-name']}>
                    <Skeleton height={14} width={160} />
                    <Skeleton height={16} width={140} />
                  </div>
                </div>
              </div>

              <div className={s['patient-item-numbers']}>
                <div className={s['patient-item-phone']}>
                  <Skeleton height={16} width={140} />
                  <Skeleton height={14} width={160} />
                </div>
                <div className={s['patient-item-phone']}>
                  <Skeleton height={16} width={140} />
                  <Skeleton height={14} width={160} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {isPatientFound &&
        isUnregisteredConversationType &&
        value.length === 0 && (
          <div className={s.empty}>
            <ChatEmptyIcon />
            <div className={s['empty-message']}>
              <div>{searchInputPlaceholder}</div>
            </div>
          </div>
        )}
      {isPatientFound && isUnregisteredConversationType && value.length > 0 && (
        <div className={s.empty}>
          <ChatEmptyIcon />
          <div className={s['empty-message']}>
            <div>{t('smsDashboard.patient_number_exist_message')}</div>
          </div>
        </div>
      )}
      {isPatientNotFound && isUnregisteredConversationType && (
        <div className={s['create-unregistered']}>
          <p>{t('smsDashboard.new_conversation')}</p>
          <p>"{value}"</p>
          <button onClick={() => handleOptionClick({ value, id: undefined })}>
            {t('smsDashboard.start_conversation')}
          </button>
        </div>
      )}

      {isPatientNotFound && !isUnregisteredConversationType && (
        <div className={s.empty}>
          <ChatEmptyIcon />
          <div className={s['empty-message']}>
            <div>{emptyMessage}</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientSearch;
